import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Tag, Sparkles } from "lucide-react";
import { toast } from "sonner";

const words = (s) => (String(s || "").toLowerCase().match(/[a-z0-9]{3,}/g) || []);
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

function rank(item, catalog) {
  const text = `${item.description} ${item.spec_or_part}`;
  const tw = new Set(words(text));
  const flat = norm(text);
  return catalog.map((p) => {
    let score = 0;
    const mpn = norm(p.mpn);
    if (mpn.length >= 4 && flat.includes(mpn)) score += 10;
    for (const w of new Set(words(`${p.name} ${p.internal_description} ${p.brand} ${p.mpn}`))) if (tw.has(w)) score += 1;
    return { p, score };
  }).filter((x) => x.score > 0).sort((a, b) => b.score - a.score).slice(0, 8);
}

function LineRow({ item, options, pick, checked, onPick, onCheck }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-3 px-3 py-3 sm:grid-cols-[auto_1fr_16rem] sm:items-center" data-testid={`bulk-sku-row-${item.id}`}>
      <Checkbox checked={checked} disabled={!pick} onCheckedChange={(v) => onCheck(!!v)} className="mt-0.5 sm:mt-0" data-testid={`bulk-sku-check-${item.id}`} />
      <div className="min-w-0">
        <div className="line-clamp-2 text-sm font-medium text-slate-800">{item.description || "Untitled line"}</div>
        {item.spec_or_part && <div className="truncate font-mono text-[11px] text-muted-foreground">{item.spec_or_part}</div>}
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Select value={pick || "none"} onValueChange={(v) => onPick(v === "none" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs" data-testid={`bulk-sku-select-${item.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Leave Unlinked</SelectItem>
            {options.map(({ p }, i) => (
              <SelectItem key={p.id} value={p.id}>
                <span className="font-mono text-[10px] font-semibold text-[#1B4383]">{p.sku}</span> {p.name}{i === 0 ? " · Best Match" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!options.length && <p className="mt-1 text-[11px] text-muted-foreground">No close match. Use Link SKU on the line.</p>}
      </div>
    </div>
  );
}

export function BulkSkuLinkDialog({ open, onClose, items, catalog, onLink }) {
  const lines = useMemo(() => items.filter((it) => !it.catalog_sku && it.line_type !== "from_stock"), [items]);
  const ranked = useMemo(() => Object.fromEntries(lines.map((it) => [it.id, rank(it, catalog)])), [lines, catalog]);
  const [picks, setPicks] = useState({});
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    const p = {}; const c = {};
    lines.forEach((it) => { const best = ranked[it.id]?.[0]; p[it.id] = best && best.score >= 2 ? best.p.id : ""; c[it.id] = !!p[it.id]; });
    setPicks(p); setChecks(c);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = lines.filter((it) => checks[it.id] && picks[it.id]);
  const allOn = chosen.length > 0 && chosen.length === lines.filter((it) => picks[it.id]).length;
  const confirm = async () => {
    setBusy(true);
    let ok = 0;
    for (const it of chosen) { if (await onLink(it.id, picks[it.id])) ok += 1; }
    setBusy(false);
    toast.success(`Linked ${ok} of ${chosen.length} line${chosen.length === 1 ? "" : "s"}`);
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="bulk-sku-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#A87F31]" />Link SKUs To Lines</DialogTitle>
          <DialogDescription>Check each suggested SKU, change it or untick the line, then confirm. Nothing is linked until you confirm.</DialogDescription>
        </DialogHeader>
        {!lines.length ? <p className="py-6 text-center text-sm text-muted-foreground">Every line already has a SKU.</p> : (<>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <label className="flex items-center gap-2"><Checkbox checked={allOn} onCheckedChange={(v) => setChecks(Object.fromEntries(lines.map((it) => [it.id, !!v && !!picks[it.id]])))} data-testid="bulk-sku-check-all" />Select All With A Match</label>
            <span data-testid="bulk-sku-selected-count">{chosen.length} of {lines.length} selected</span>
          </div>
          <div className="max-h-[55vh] divide-y divide-border overflow-y-auto rounded-lg border border-border">
            {lines.map((it) => (
              <LineRow key={it.id} item={it} options={ranked[it.id] || []} pick={picks[it.id]} checked={!!checks[it.id]}
                onPick={(v) => { setPicks((s) => ({ ...s, [it.id]: v })); setChecks((s) => ({ ...s, [it.id]: !!v })); }}
                onCheck={(v) => setChecks((s) => ({ ...s, [it.id]: v }))} />
            ))}
          </div>
        </>)}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={!chosen.length || busy} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="bulk-sku-confirm">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Tag className="mr-2 h-4 w-4" />}Link {chosen.length || ""} SKU{chosen.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
